/* global globalThis */

import trace from '~/lib/trace';
import React, {useState, useCallback, useEffect, useContext} from 'react';
import {View, ScrollView} from 'react-native';
import {Divider} from 'react-native-paper';

import sy from '~/styles';
import UserContext from '~/components/context/UserContext';
import {useDispatch, useSelector} from '~/lib/hooks';
import {user as userApi} from '~/api/private';
import {setUserSettings} from '~/actions';
import {SOURCES} from '~/types/sources';

import {
  Modal,
  Pressable,
  Text,
  Link,
  RadioButton,
  ActionButton,
  DealerPersona,
  TabBar,
} from '~/components/controls';
import {Grouping, Switch} from '~/components/controls/3';
import Actionsheet, {
  Title,
  Content,
  Actions,
} from '~/components/controls/Actionsheet';
import withDimensions from '~/components/hoc/with-dimensions';

import CloseIcon from '~/images/md-icons/close/materialicons/24px.svg';
import SettingsIcon from '~/images/md-icons/settings/materialicons/24px.svg';
import LocalShippingIcon from '~/images/md-icons/local_shipping/materialsymbolsoutlined/24px.svg';
import NotificationsIcon from '~/images/md-icons/notifications/materialsymbolsoutlined/24px.svg';
import SettingsApplicationsIcon from '~/images/md-icons/settings_applications/materialsymbolsoutlined/24px.svg';
import DragHandleIcon from '~/images/md-icons/drag_handle/materialsymbolsoutlined/24px.svg';
import ManageAccountsIcon from '~/images/md-icons/manage_accounts/materialicons/24px.svg';
import ManageAccountsOutlinedIcon from '~/images/md-icons/manage_accounts/materialsymbolsoutlined/24px.svg';
import WarehouseIcon from '~/images/md-icons/warehouse/materialicons/24px.svg';
import WarehouseOutlinedIcon from '~/images/md-icons/warehouse/materialsymbolsoutlined/24px.svg';
import PropTypes from 'prop-types';

const InitialTabActionsheet = ({
  user_settings,
  visible,
  onDismiss,
  onRefresh,
}) => (
  <Actionsheet visible={visible} onDismiss={onDismiss}>
    <Title>
      <Text style={[sy.largePlus, {lineHeight: 30}]}>Startpagina</Text>
    </Title>
    <Divider style={sy['bg-gray88']} />
    <Content style={{padding: 0}}>
      <Pressable
        style={[sy['p-4'], sy['flex-row'], sy['items-center'], sy['gap-8']]}
        onPress={async () => {
          await userApi.update_settings({
            ...user_settings,
            initial_tab: 'Messages',
          });

          onRefresh();
        }}>
        <RadioButton checked={user_settings.initial_tab === 'Messages'} />
        <Text>Berichten</Text>
      </Pressable>
      <Divider style={[sy['ml-18'], sy['bg-gray95']]} />
      <Pressable
        style={[sy['p-4'], sy['flex-row'], sy['items-center'], sy['gap-8']]}
        onPress={async () => {
          await userApi.update_settings({
            ...user_settings,
            initial_tab: 'Search',
          });

          onRefresh();
        }}>
        <RadioButton checked={user_settings.initial_tab === 'Search'} />
        <Text>Opdrachten</Text>
      </Pressable>
      <Divider style={[sy['ml-18'], sy['bg-gray95']]} />
      <Pressable
        style={[sy['p-4'], sy['flex-row'], sy['items-center'], sy['gap-8']]}
        onPress={async () => {
          await userApi.update_settings({
            ...user_settings,
            initial_tab: 'Calendar',
          });

          onRefresh();
        }}>
        <RadioButton checked={user_settings.initial_tab === 'Calendar'} />
        <Text>Kalender</Text>
      </Pressable>
    </Content>
    <Divider style={sy['bg-gray88']} />
    <Actions>
      <ActionButton onPress={onDismiss}>OK</ActionButton>
    </Actions>
  </Actionsheet>
);
InitialTabActionsheet.propTypes = {
  user_settings: PropTypes.any,
  visible: PropTypes.any,
  onDismiss: PropTypes.any,
  onRefresh: PropTypes.any,
};

const TITLE_HEIGHT_IN_PX = 71;
const ACTIONS_HEIGHT_IN_PX = 51;

const DefaultDealerActionsheet = withDimensions(
  ({dimensions, dealers, visible, onDismiss, onRefresh}) => (
    <Actionsheet visible={visible} onDismiss={onDismiss}>
      <Title>
        <Text style={[sy.largePlus, {lineHeight: 30}]}>Standaardvestiging</Text>
      </Title>
      <Divider style={sy['bg-gray88']} />
      <Content
        style={{
          padding: 0,
          maxHeight:
            dimensions.window.height -
            TITLE_HEIGHT_IN_PX -
            ACTIONS_HEIGHT_IN_PX,
        }}>
        {dealers
          .sort((a, b) => a.city.localeCompare(b.city))
          .map((dealer, index, arr) => (
            <React.Fragment key={dealer.id}>
              <Pressable
                style={[
                  sy['p-4'],
                  sy['flex-row'],
                  sy['items-center'],
                  sy['gap-4'],
                ]}
                onPress={async () => {
                  await userApi.update({default_dealer: dealer.id});
                  onRefresh();
                }}>
                <RadioButton checked={dealer.is_default} />
                <View style={[sy['flex-1']]}>
                  <Text style={[sy['truncate']]}>
                    {dealer.city}&nbsp;&middot;&nbsp;{dealer.short_name}
                  </Text>
                </View>
                <DealerPersona dealer_id={dealer.id} size={24} />
              </Pressable>
              {index < arr.length - 1 && (
                <Divider style={[sy['bg-gray95'], sy['mx-4']]} />
              )}
            </React.Fragment>
          ))}
      </Content>
      <Divider style={sy['bg-gray88']} />
      <Actions>
        <ActionButton onPress={onDismiss}>OK</ActionButton>
      </Actions>
    </Actionsheet>
  ),
);

const Tabs = {
  User: 'Gebruiker',
  Dealer: 'Vestiging',
};

const SettingsModal = ({visible, onDismiss}) => {
  const dispatch = useDispatch();
  const {me, update: refreshMe} = useContext(UserContext);
  const [articleSources, setArticleSources] = useState([SOURCES.LKQ]);
  const [selectedTab, setSelectedTab] = useState(Tabs.User);

  useEffect(() => {
    const {dealers} = me ?? {};

    if (dealers?.some((dealer) => !!dealer.package_access?.includes('aag'))) {
      setArticleSources((prevState) =>
        prevState.includes(SOURCES.PartsPoint)
          ? prevState
          : [...prevState, SOURCES.PartsPoint],
      );
    }

    const vrooam = dealers
      ?.find((dealer) =>
        dealer.package_access?.find((pkg) => SOURCES.Vrooam(pkg)),
      )
      ?.package_access?.find((pkg) => SOURCES.Vrooam(pkg));

    if (vrooam) {
      setArticleSources((prevState) => [...new Set([...prevState, vrooam])]);
    }
  }, [me]);

  const refresh = useCallback(() => {
    const fetch = async () => {
      const {user_settings} = await userApi.me();
      dispatch(setUserSettings(user_settings?.partner ?? {}));
    };

    fetch().catch(trace);
  }, [dispatch]);

  const requestPermission = async () => {
    const requestPermissionFromApi = async () => {
      if (!globalThis.Notification) {
        return undefined;
      }

      try {
        return await globalThis.Notification.requestPermission();
      } catch {
        return await new Promise((resolve, reject) => {
          const permission = globalThis.Notification.requestPermission(resolve);
          if (permission?.then) {
            permission.then(resolve, reject);
          }
        });
      }
    };

    const permission = await requestPermissionFromApi();

    console.log(permission);
  };

  const [showInitialTabActionsheet, setShowInitialTabActionsheet] =
    useState(false);
  const [showDefaultDealerActionsheet, setShowDefaultDealerActionsheet] =
    useState(false);

  const user_settings = useSelector((state) => state.user_settings) ?? {};
  const default_dealer = me?.dealers?.find((dealer) => dealer.is_default);
  let startPageLabel = 'Opdrachten';
  if (user_settings.initial_tab === 'Messages') {
    startPageLabel = 'Berichten';
  } else if (user_settings.initial_tab === 'Calendar') {
    startPageLabel = 'Kalender';
  }

  return (
    <>
      <InitialTabActionsheet
        user_settings={user_settings}
        visible={showInitialTabActionsheet}
        onDismiss={() => setShowInitialTabActionsheet(false)}
        onRefresh={refresh}
      />
      <DefaultDealerActionsheet
        dealers={me?.dealers ?? []}
        visible={showDefaultDealerActionsheet}
        onDismiss={() => setShowDefaultDealerActionsheet(false)}
        onRefresh={() => {
          refreshMe();
        }}
      />
      <Modal visible={visible} onDismiss={onDismiss}>
        <View
          style={[
            {height: 64},
            sy['flex-row'],
            sy['items-center'],
            sy['gap-8'],
          ]}>
          <Pressable style={[sy['p-4']]} onPress={onDismiss}>
            <CloseIcon fill="#4a4a49" />
          </Pressable>
          <View
            style={[sy['flex-1'], sy['items-center'], sy['justify-center']]}>
            <Text>Instellingen</Text>
          </View>
          <View style={[sy['p-4']]}>
            <SettingsIcon fill="#4a4a49" />
          </View>
        </View>
        <TabBar tab={selectedTab} onChangeTab={setSelectedTab}>
          <TabBar.Tab
            label={Tabs.User}
            icon={{
              filled: <ManageAccountsIcon />,
              outlined: <ManageAccountsOutlinedIcon />,
            }}
          />
          <TabBar.Tab
            label={Tabs.Dealer}
            icon={{
              filled: <WarehouseIcon />,
              outlined: <WarehouseOutlinedIcon />,
            }}
          />
        </TabBar>
        <Divider style={sy['bg-gray88']} />
        <TabBar.Content tab={selectedTab}>
          <ScrollView
            tab={Tabs.User}
            style={[sy['bg-gray95']]}
            contentContainerStyle={[sy['p-2'], sy['gap-2']]}>
            <Grouping
              icon={<SettingsApplicationsIcon fill="#4a4a49" />}
              header="Algemeen"
              initial={{expand: true}}
              options={{topMost: true}}>
              <View style={[sy['p-4'], sy['flex-row'], sy['justify-between']]}>
                <Text>Startpagina</Text>
                <Link onPress={() => setShowInitialTabActionsheet(true)}>
                  {startPageLabel}
                </Link>
              </View>
              <Divider style={sy['bg-gray95']} />
              <View style={[sy['p-4'], sy['flex-row'], sy['justify-between']]}>
                <Text>Standaardvestiging</Text>
                <Link onPress={() => setShowDefaultDealerActionsheet(true)}>
                  {default_dealer?.city ?? 'Keuze maken'}
                </Link>
              </View>
              <Divider style={sy['bg-gray95']} />
              <View style={[sy['p-4'], sy['flex-row'], sy['justify-between']]}>
                <View>
                  <Text>Voertuignummer</Text>
                  <Text style={[sy.small, sy['text-lightgray']]}>
                    Aanmaken, zoeken en weergave opdracht
                  </Text>
                </View>
                <Switch
                  onValueChange={async (value) => {
                    await userApi.update_settings({
                      ...user_settings,
                      search_by_vehicle_number: value,
                    });

                    refresh();
                  }}
                  value={user_settings.search_by_vehicle_number}
                />
              </View>
            </Grouping>

            <Grouping
              icon={<NotificationsIcon fill="#4a4a49" />}
              header="Meldingen"
              initial={{expand: true}}>
              <View style={[sy['p-4'], sy['flex-row'], sy['justify-between']]}>
                <View>
                  <Text>Afkeuring</Text>
                  <Text style={[sy.small, sy['text-lightgray']]}>Push</Text>
                </View>
                <Switch
                  onValueChange={async (value) => {
                    if (value) {
                      await requestPermission();
                    }

                    await userApi.update_settings({
                      ...user_settings,
                      push_notifications: {
                        ...user_settings.push_notifications,
                        denied: value,
                      },
                    });

                    refresh();
                  }}
                  value={user_settings.push_notifications?.denied}
                />
              </View>
              <Divider style={sy['bg-gray95']} />
              <View style={[sy['p-4'], sy['flex-row'], sy['justify-between']]}>
                <View>
                  <Text>Goedkeuring</Text>
                  <Text style={[sy.small, sy['text-lightgray']]}>Push</Text>
                </View>
                <Switch
                  onValueChange={async (value) => {
                    if (value) {
                      await requestPermission();
                    }

                    await userApi.update_settings({
                      ...user_settings,
                      push_notifications: {
                        ...user_settings.push_notifications,
                        approved: value,
                      },
                    });

                    refresh();
                  }}
                  value={user_settings.push_notifications?.approved}
                />
              </View>
              <Divider style={sy['bg-gray95']} />
              <View style={[sy['p-4'], sy['flex-row'], sy['justify-between']]}>
                <View>
                  <Text>Nieuw bericht</Text>
                  <Text style={[sy.small, sy['text-lightgray']]}>Push</Text>
                </View>
                <Switch
                  onValueChange={async (value) => {
                    if (value) {
                      await requestPermission();
                    }

                    await userApi.update_settings({
                      ...user_settings,
                      push_notifications: {
                        ...user_settings.push_notifications,
                        message: value,
                      },
                    });

                    refresh();
                  }}
                  value={user_settings.push_notifications?.message}
                />
              </View>
              <Divider style={sy['bg-gray95']} />
              <View style={[sy['p-4'], sy['flex-row'], sy['justify-between']]}>
                <View>
                  <Text>Doorbelasting kosten TecRMI</Text>
                  <Text style={[sy.small, sy['text-lightgray']]}>
                    Pop-up tonen
                  </Text>
                </View>
                <Switch
                  onValueChange={async () => {
                    await userApi.update_settings({
                      ...user_settings,
                      tecrmi_info_dialog_never_show:
                        !user_settings.tecrmi_info_dialog_never_show,
                    });

                    refresh();
                  }}
                  value={!user_settings.tecrmi_info_dialog_never_show}
                />
              </View>
              <Divider style={sy['bg-gray95']} />
              <View style={[sy['p-4'], sy['flex-row'], sy['justify-between']]}>
                <View>
                  <Text>Notificatie onderdeel apart toegevoegd</Text>
                  <Text style={[sy.small, sy['text-lightgray']]}>
                    Pop-up tonen
                  </Text>
                </View>
                <Switch
                  onValueChange={async () => {
                    await userApi.update_settings({
                      ...user_settings,
                      never_show_parts_split_dialog:
                        !user_settings.never_show_parts_split_dialog,
                    });

                    refresh();
                  }}
                  value={!user_settings.never_show_parts_split_dialog}
                />
              </View>
              <Divider style={sy['bg-gray95']} />
              <View style={[sy['p-4'], sy['flex-row'], sy['justify-between']]}>
                <View>
                  <Text>Vraag tonen extra services onderhoud</Text>
                  <Text style={[sy.small, sy['text-lightgray']]}>
                    Pop-up tonen
                  </Text>
                </View>
                <Switch
                  onValueChange={async () => {
                    await userApi.update_settings({
                      ...user_settings,
                      never_show_maintenance_extras_dialog:
                        !user_settings.never_show_maintenance_extras_dialog,
                    });

                    refresh();
                  }}
                  value={!user_settings.never_show_maintenance_extras_dialog}
                />
              </View>
              <Divider style={sy['bg-gray95']} />
              <View style={[sy['p-4'], sy['flex-row'], sy['justify-between']]}>
                <View>
                  <Text>Bevestigen laten vervallen activiteit</Text>
                  <Text style={[sy.small, sy['text-lightgray']]}>
                    Pop-up tonen
                  </Text>
                </View>
                <Switch
                  onValueChange={async () => {
                    await userApi.update_settings({
                      ...user_settings,
                      never_show_rob_task_cancel_dialog:
                        !user_settings.never_show_rob_task_cancel_dialog,
                    });

                    refresh();
                  }}
                  value={!user_settings.never_show_rob_task_cancel_dialog}
                />
              </View>
            </Grouping>
          </ScrollView>
          <ScrollView
            tab={Tabs.Dealer}
            style={[sy['bg-gray95']]}
            contentContainerStyle={[sy['p-2'], sy['gap-2']]}>
            <Grouping
              icon={<LocalShippingIcon fill="#4a4a49" />}
              header="Artikelen"
              initial={{expand: true}}>
              {articleSources.some((src) => SOURCES.Vrooam(src)) ? (
                <>
                  <View
                    style={[sy['p-4'], sy['flex-row'], sy['justify-between']]}>
                    <View
                      style={[sy['flex-row'], sy['items-center'], sy['gap-4']]}>
                      <DragHandleIcon fill="#4a4a49" style={{opacity: 0.4}} />
                      <Text>
                        {
                          SOURCES[
                            articleSources.find((src) => SOURCES.Vrooam(src))
                          ]
                        }
                      </Text>
                    </View>
                    <Switch disabled={true} value={true} />
                  </View>
                  <Divider style={sy['bg-gray95']} />
                </>
              ) : null}
              <View style={[sy['p-4'], sy['flex-row'], sy['justify-between']]}>
                <View style={[sy['flex-row'], sy['items-center'], sy['gap-4']]}>
                  <DragHandleIcon fill="#4a4a49" style={{opacity: 0.4}} />
                  <Text>LKQ</Text>
                </View>
                <Switch
                  disabled={true}
                  value={articleSources.includes(SOURCES.LKQ)}
                />
              </View>
              <Divider style={sy['bg-gray95']} />
              <View style={[sy['p-4'], sy['flex-row'], sy['justify-between']]}>
                <View style={[sy['flex-row'], sy['items-center'], sy['gap-4']]}>
                  <DragHandleIcon fill="#4a4a49" style={{opacity: 0.4}} />
                  <Text>PartsPoint</Text>
                </View>
                <Switch
                  disabled={true}
                  value={articleSources.includes(SOURCES.PartsPoint)}
                />
              </View>
            </Grouping>
          </ScrollView>
        </TabBar.Content>
      </Modal>
    </>
  );
};
SettingsModal.propTypes = {
  visible: PropTypes.any,
  onDismiss: PropTypes.any,
};

export default SettingsModal;
